import { create } from "zustand";
import { setSessionToken } from "../api/client";

// Auth state. The session token is persisted in sessionStorage (per-tab, cleared when the
// tab closes) so a page reload keeps you signed in — but NOT in localStorage (§20). The
// vault keys are never persisted: after a reload the vault is locked and the master
// password is required again.
const KEY = "vault.session";

interface Persisted {
  token: string;
  login: string;
}

function loadPersisted(): { token: string | null; login: string | null } {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Persisted;
      setSessionToken(p.token);
      return { token: p.token, login: p.login };
    }
  } catch {
    /* ignore */
  }
  return { token: null, login: null };
}

const initial = loadPersisted();

interface AuthState {
  token: string | null;
  login: string | null;
  setSession: (token: string, login: string) => void;
  clear: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  token: initial.token,
  login: initial.login,
  setSession: (token, login) => {
    setSessionToken(token);
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ token, login }));
    } catch {
      /* ignore */
    }
    set({ token, login });
  },
  clear: () => {
    setSessionToken(null);
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    set({ token: null, login: null });
  },
}));
